import React from 'react'
import { Alert, Button, Grid, TextField, Typography } from '@mui/material'
import { FormikProps, useFormikContext } from 'formik'
import { CustomerInfoTemplate } from '../CustomerInfoTemplate/CustomerInfoTemplate'
import {
  FlyoutProductBuyerFormFields,
  FlyoutProductInfoCollectionFormMetaData,
  FlyoutProductSections,
} from '../../../types/addEditProduct'
import {
  defaultBuyerFormFields,
  defaultButtonColor,
  defaultButtonTextColor,
} from '../../utils/constants'
import { useStyles } from './CustomerCollectionForm.styles'

export interface CustomerFormProps {
  collectionData: FlyoutProductInfoCollectionFormMetaData
  classes: ReturnType<typeof useStyles>['classes']
}

export const CustomerForm: React.FC<CustomerFormProps> = ({
  collectionData,
  classes,
}) => {
  const clickPolicy = () => {
    const privacyPolicyLink = collectionData.privacyPolicyLink
    if (privacyPolicyLink) {
      window.open(privacyPolicyLink, '_blank')
    }
  }

  const combinedBuyerFormFields = [
    ...defaultBuyerFormFields,
    ...(collectionData?.buyerFormFields || []),
  ]
  const returnFields = (fields: FlyoutProductBuyerFormFields, grid: 6 | 12) => {
    return (
      <Grid
        key={fields.title}
        size={{
          xs: 12,
          sm: grid,
          md: grid,
        }}
      >
        <TextField
          className={classes.textField}
          label={`${fields.title}${fields.isRequired ? '*' : ''}`}
          name={fields.title}
          fullWidth
          variant="standard"
          slotProps={{
            htmlInput: {
              'data-input-name': `custom-input-${fields.title}`,
              'data-testid': `field-${fields.title}`,
              'data-id': `field-${fields.title}`,
            },
          }}
        />
      </Grid>
    )
  }
  return (
    <div>
      <Typography className={classes.headingMessage}>
        {collectionData.headingMessage ? (
          collectionData.headingMessage
        ) : (
          <Typography>
            Welcome! Fill the information below to set up your account!{' '}
          </Typography>
        )}
      </Typography>
      <Alert
        severity="info"
        className={classes.customAlert}
        data-testid="alert-info"
      >
        This marketplace purchase is not complete until this form has been
        submitted.
      </Alert>
      {collectionData?.buyerFormFields ? (
        <form data-testid="custom-form">
          <Grid
            container
            spacing={4}
            className={classes.customFormInnerContainer}
          >
            {combinedBuyerFormFields
              .slice(0, 2)
              .map((fields: FlyoutProductBuyerFormFields) =>
                returnFields(fields, 6)
              )}
            {combinedBuyerFormFields
              .slice(2)
              .map((fields: FlyoutProductBuyerFormFields) =>
                returnFields(fields, 12)
              )}
          </Grid>
        </form>
      ) : (
        <></>
      )}
      <div>
        <Typography className={classes.policy}>
          By clicking the Submit button, I agree that my information will be
          shared with Labra and Labra.io in accordance with this{' '}
          <a
            onClick={clickPolicy}
            target="_blank"
            data-testid="policy-link"
            className={classes.link}
            rel="noreferrer"
          >
            Privacy Policy
          </a>
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          type="submit"
          data-testid="submit-button"
          disabled={false}
          className={classes.button}
          onClick={event => event.preventDefault()}
        >
          Submit
        </Button>
      </div>
    </div>
  )
}

export const CustomerCollectionForm: React.FC = () => {
  const { values } = useFormikContext() as FormikProps<FlyoutProductSections>
  const infoCollectionFormMetaData = values.infoCollectionFormMetaData
  const generalInformation = values.generalInformation
  const bgColor = computeBackgroundColor(infoCollectionFormMetaData.bgColor)
  const buttonColor = computeBackgroundColor(
    infoCollectionFormMetaData.buttonColor
  )
  const buttonTextColor = computeBackgroundColor(
    infoCollectionFormMetaData.buttonTextColor
  )

  const { classes } = useStyles({
    btnColor: buttonColor || defaultButtonColor,
    buttonTextColor: buttonTextColor || defaultButtonTextColor,
  })

  return (
    <div data-testid="customer-collection-form-container">
      <CustomerInfoTemplate
        children={
          <CustomerForm
            collectionData={infoCollectionFormMetaData}
            classes={classes}
          />
        }
        logoUrl={generalInformation.productLogoUrl}
        backgroundImgUrl={infoCollectionFormMetaData.bgImageUrl}
        bgColor={bgColor}
      />
    </div>
  )
}

const computeBackgroundColor = (color: string | undefined): string => {
  const bgColorType = color?.includes('#') ? 'HEX' : 'RGB'
  if (bgColorType === 'RGB') {
    const rgbValues = color
      ?.replace(/[rgba()]/g, '')
      .split(',')
      .map(value => parseFloat(value.trim())) || [255, 255, 255, 1]
    const [r = 255, g = 255, b = 255, a = 1] = rgbValues
    const alpha = a / 100
    return `rgba(${r}, ${g}, ${b}, ${alpha})`
  } else {
    return color || ''
  }
}

export default CustomerCollectionForm
